import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X, CheckCircle2, MessageCircle, Search } from "lucide-react";
import { company } from "../mock";
import { trackConversion, CONV } from "../lib/gtag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/catalog`;
const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><rect width='120' height='120' fill='#eef2f6'/><text x='50%' y='50%' font-family='Arial' font-size='12' fill='#94a3b8' text-anchor='middle' dominant-baseline='middle'>DEA</text></svg>"
  );

const fmtPrice = (n) => (n == null ? "-" : "€ " + n.toLocaleString("it-IT"));

export default function GeneralQuoteForm({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [engine, setEngine] = useState(null);

  const [form, setForm] = useState({ name: "", phone: "", email: "", note: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const debounceRef = useRef(null);
  const acRef = useRef(null);

  // Reset everything when opened/closed
  useEffect(() => {
    if (open) {
      setQuery(""); setResults([]); setShowResults(false); setEngine(null);
      setForm({ name: "", phone: "", email: "", cf_piva: "", shipping_address: "", note: "" });
      setErrors({}); setSending(false); setDone(false);
    }
  }, [open]);

  useEffect(() => {
    const onClickOutside = (e) => { if (acRef.current && !acRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (engine) return; // don't search when an engine is already chosen
    if (query.trim().length < 2) { setResults([]); setShowResults(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/engines`, { params: { search: query.trim(), limit: 6 } });
        setResults(r.data.items);
        setShowResults(true);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, engine]);

  if (!open) return null;

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const pickEngine = (e) => { setEngine(e); setShowResults(false); setQuery(""); };
  const changeEngine = () => { setEngine(null); setQuery(""); setResults([]); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Inserisci nome e cognome / officina";
    if (!form.phone.trim()) e.phone = "Inserisci un numero di telefono";
    else if (!/^[+]?[\d\s().-]{6,}$/.test(form.phone.trim())) e.phone = "Numero non valido";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Email non valida";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildWaMessage = () => {
    const lines = [`Ciao ${company.name}, vorrei un preventivo gratuito:`];
    if (engine) {
      lines.push(`• Motore: ${engine.titolo}`);
      if (engine.codice_motore) lines.push(`• Codice: ${engine.codice_motore}`);
      lines.push(`• Prezzo: ${fmtPrice(engine.prezzo_vendita)}`);
    } else if (query.trim()) {
      lines.push(`• Codice/ricerca motore: ${query.trim()}`);
    }
    lines.push("");
    lines.push(`Nome/Officina: ${form.name}`);
    lines.push(`Telefono: ${form.phone}`);
    if (form.email) lines.push(`Email: ${form.email}`);
    if (form.note) lines.push(`Note: ${form.note}`);
    return encodeURIComponent(lines.join("\n"));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        note: form.note.trim() || null,
        engine_id: engine ? engine.id : null,
        engine_title: engine ? engine.titolo : null,
        engine_code: engine ? engine.codice_motore : (query.trim() || null),
        engine_price: engine ? engine.prezzo_vendita : null,
      });
    } catch (err) {
      console.error("Lead save failed", err);
    } finally {
      setSending(false);
      setDone(true);
      trackConversion(CONV.preventivo);
    }
  };

  const openWhatsApp = () => {
    trackConversion(CONV.contatto);
    window.open(`https://wa.me/${company.wa}?text=${buildWaMessage()}`, "_blank");
    onClose();
  };

  return (
    <div className="engine-modal-overlay" onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}>
      <div className="wa-modal" style={{ maxWidth: 480 }}>
        <button className="wa-modal-close" onClick={onClose} aria-label="Chiudi"><X size={18} /></button>

        {!done ? (
          <>
            <h2 className="wa-modal-h2">Richiedi preventivo gratuito</h2>

            {engine ? (
              <div className="eq-form-summary">
                <img src={engine.foto_url || FALLBACK_IMG} alt={engine.titolo}
                  onError={(e) => { e.target.src = FALLBACK_IMG; }} />
                <div>
                  <div className="eq-t">{engine.titolo}</div>
                  <div className="eq-p">{fmtPrice(engine.prezzo_vendita)}</div>
                  <button type="button" className="eq-summary-change" onClick={changeEngine}>✕ Cambia motore</button>
                </div>
              </div>
            ) : (
              <div className="eq-autocomplete" ref={acRef}>
                <label className="eq-field" style={{ display: "block", fontWeight: 700, color: "var(--navy)", fontSize: "0.85rem", marginBottom: "0.35rem" }}>
                  Trova il tuo motore dal catalogo <span style={{ color: "var(--grey)", fontWeight: 400 }}>(opzionale)</span>
                </label>
                <div className="eq-ac-input">
                  <Search size={17} />
                  <input value={query} onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => { if (results.length) setShowResults(true); }}
                    placeholder="Scrivi il codice motore o il modello…" />
                </div>
                {showResults && (
                  <div className="eq-ac-results">
                    {searching ? (
                      <div className="eq-ac-empty">Ricerca in corso…</div>
                    ) : results.length === 0 ? (
                      <div className="eq-ac-empty">Nessun motore trovato. Puoi comunque proseguire e indicarci il codice nelle note.</div>
                    ) : (
                      results.map((e) => (
                        <div className="eq-ac-item" key={e.id} onClick={() => pickEngine(e)}>
                          <img src={e.foto_url || FALLBACK_IMG} alt={e.titolo} onError={(ev) => { ev.target.src = FALLBACK_IMG; }} />
                          <div>
                            <div className="t">{e.titolo}</div>
                            <div className="m">{e.marca}{e.codice_motore ? ` · ${e.codice_motore}` : ""}</div>
                          </div>
                          <div className="p">{fmtPrice(e.prezzo_vendita)}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={submit} noValidate>
              <div className="eq-field">
                <label>Nome e Cognome / Officina <span className="req">*</span></label>
                <input className={errors.name ? "error" : ""} value={form.name}
                  onChange={(e) => set("name", e.target.value)} placeholder="Es. Mario Rossi / Autofficina Rossi" />
                {errors.name && <div className="err-msg">{errors.name}</div>}
              </div>
              <div className="eq-field">
                <label>Telefono <span className="req">*</span></label>
                <input className={errors.phone ? "error" : ""} value={form.phone} inputMode="tel"
                  onChange={(e) => set("phone", e.target.value)} placeholder="Es. 333 1234567" />
                {errors.phone && <div className="err-msg">{errors.phone}</div>}
              </div>
              <div className="eq-field">
                <label>Email <span style={{ color: "var(--grey)", fontWeight: 400 }}>(opzionale)</span></label>
                <input className={errors.email ? "error" : ""} type="email" value={form.email}
                  onChange={(e) => set("email", e.target.value)} placeholder="La tua email" />
                {errors.email && <div className="err-msg">{errors.email}</div>}
              </div>
              <div className="eq-field">
                <label>Note / Dettagli <span style={{ color: "var(--grey)", fontWeight: 400 }}>(opzionale)</span></label>
                <textarea rows={3} value={form.note} onChange={(e) => set("note", e.target.value)}
                  placeholder="Marca, modello, anno, codice P5, urgenza…" />
              </div>
              <p className="eq-privacy">Inviando accetti il trattamento dei dati per la sola risposta alla richiesta.</p>
              <button type="submit" className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center" }} disabled={sending}>
                <MessageCircle size={18} /> {sending ? "Invio…" : "Invia richiesta"}
              </button>
            </form>
          </>
        ) : (
          <div className="eq-success">
            <div className="icon"><CheckCircle2 size={36} color="#16a34a" /></div>
            <h3>Richiesta registrata!</h3>
            <p>Grazie {form.name.split(" ")[0]}! Abbiamo salvato la tua richiesta{engine ? <> per <strong>{engine.titolo}</strong></> : null}. Prosegui su WhatsApp per inviarcela subito e ricevere risposta in tempi rapidi.</p>
            <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center" }} onClick={openWhatsApp}>
              <MessageCircle size={18} /> Continua su WhatsApp
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
